import { defineComponent as _defineComponent } from 'vue'
import { renderList as _renderList, Fragment as _Fragment, openBlock as _openBlock, createElementBlock as _createElementBlock, normalizeStyle as _normalizeStyle, createElementVNode as _createElementVNode, withModifiers as _withModifiers, normalizeClass as _normalizeClass, createCommentVNode as _createCommentVNode, createBlock as _createBlock, toDisplayString as _toDisplayString } from "vue"

const _hoisted_1 = { class: "rec-ref__row-wrap" }
const _hoisted_2 = {
  key: 1,
  class: "rec-ref__chevron-placeholder"
}
const _hoisted_3 = { class: "rec-ref__label" }

import { computed, ref, watch } from 'vue';
import MostaCheckbox from '@/components/common/basic/MostaCheckbox.vue';
import { VariableType } from '@/models/etc/types';
import { buildHighlightSpans } from './composables/useRecursiveSearchMap';
import { isNodeSelected, toggleSelection } from './composables/useRecursiveSelection';
import { treeGuideLineLeft as calcTreeGuideLineLeft } from '@/components/common/tree/treeGuideLines';


export default /*@__PURE__*/_defineComponent({
  __name: 'RecursiveSelectorNode',
  props: {
    node: {},
    modelValue: {},
    rowKey: {},
    depth: {},
    hasChildren: { type: Boolean },
    hasRawChildren: { type: Boolean },
    expanded: { type: Boolean },
    active: { type: Boolean },
    label: {},
    tokens: {},
    allowMultiple: { type: Boolean, default: false },
    takeIdsOnly: { type: Boolean, default: false },
    hideCheckboxes: { type: Boolean, default: false },
    showRecords: { type: Boolean, default: true },
    isColumn: { type: Boolean, default: false },
    returnType: { default: 'single-element' },
    elementType: { default: VariableType.Entity },
    nestedKeyName: {},
    entityInsideName: { default: '' },
    chain: {}
  },
  emits: ["update:modelValue", "toggleExpand", "elementToggled", "rowMounted"],
  setup(__props: any, { expose: __expose, emit: __emit }) {

function guideLineLeft(level: number): number {
  return calcTreeGuideLineLeft(level);
}

const props = __props;

const emit = __emit;

const rowEl = ref<HTMLElement | null>(null);
watch(rowEl, (el) => emit('rowMounted', { key: props.rowKey, el }), { immediate: true });

// Признак того, что узел в принципе выбираемый по семантике дерева
// (лист, либо разворачиваемая ветка в режиме без records). Не зависит от
// видимости чекбокса — нужен и для клика по строке при hideCheckboxes=true.
const isSelectable = computed(() => {
  if (props.returnType === 'none') return !!props.node?.Selectable || (props.hasChildren && !props.showRecords);
  return !props.hasRawChildren || (props.hasChildren && !props.showRecords);
});

const showCheckbox = computed(() => {
  if (props.hideCheckboxes) return false;
  return isSelectable.value;
});

const selected = computed(() => isNodeSelected(props.node, props.modelValue, {
  returnType: props.returnType,
  isColumn: props.isColumn,
  elementType: props.elementType,
  takeIdsOnly: props.takeIdsOnly,
  entityInsideName: props.entityInsideName,
  chain: props.chain,
}));

const nameSpans = computed(() => buildHighlightSpans(props.label || '', props.tokens || []));

function updateSelection(checked: boolean) {
  const next = toggleSelection(props.node, checked, props.modelValue || [], {
    returnType: props.returnType,
    allowMultiple: props.allowMultiple,
    chain: props.chain,
    nestedKeyName: props.nestedKeyName,
    isColumn: props.isColumn,
    elementType: props.elementType,
    takeIdsOnly: props.takeIdsOnly,
    entityInsideName: props.entityInsideName,
  });

  // in none mode we still emit cloned model to keep parent watchers compatible
  emit('update:modelValue', props.returnType === 'none' ? [...(props.modelValue || [])] : next);
  emit('elementToggled', checked);
}

function onCheckboxUpdate(value: any) {
  updateSelection(!!value);
}

function onRowClick() {
  // По клику на подпись: если есть шеврон — ведём себя как клик по шеврону.
  if (props.hasChildren) {
    emit('toggleExpand', props.rowKey);
    return;
  }
  // Клик по строке всегда переключает выбор для выбираемых узлов,
  // даже если чекбокс скрыт (legacy-поведение, нужно для FormulaSelector).
  if (!isSelectable.value) return;
  updateSelection(!selected.value);
}

function toggle() {
  updateSelection(!selected.value);
}

function scrollIntoView(viewport?: HTMLElement | null) {
  if (!rowEl.value) return;
  rowEl.value.scrollIntoView({ behavior: 'smooth', block: 'center' });
  if (!viewport) return;
}

__expose({ toggle, scrollIntoView });

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", _hoisted_1, [
    (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(_ctx.depth, (level) => {
      return (_openBlock(), _createElementBlock("span", {
        key: 'guide-' + level,
        class: "rec-ref__guide",
        style: _normalizeStyle({ left: guideLineLeft(level - 1) + 'px' })
      }, null, 4))
    }), 128)),
    _createElementVNode("div", {
      ref_key: "rowEl",
      ref: rowEl,
      class: _normalizeClass(["rec-ref__row", { 'rec-ref__row--active': _ctx.active }]),
      style: _normalizeStyle({ paddingLeft: `${_ctx.depth * 22}px` }),
      onClick: _withModifiers(onRowClick, ["stop","prevent"])
    }, [
      (_ctx.hasChildren)
        ? (_openBlock(), _createElementBlock("span", {
            key: 0,
            class: _normalizeClass(["rec-ref__chevron", { 'rec-ref__chevron--closed': !_ctx.expanded }]),
            onClick: _cache[0] || (_cache[0] = _withModifiers(($event: any) => (emit('toggleExpand', _ctx.rowKey)), ["stop","prevent"]))
          }, _cache[2] || (_cache[2] = [
            _createElementVNode("svg", {
              class: "rec-ref__chevron-icon",
              width: "16",
              height: "16",
              viewBox: "0 0 16 16",
              fill: "none",
              xmlns: "http://www.w3.org/2000/svg"
            }, [
              _createElementVNode("path", {
                d: "M4 6L8 10L12 6",
                stroke: "currentColor",
                "stroke-width": "2",
                "stroke-linecap": "round",
                "stroke-linejoin": "round"
              })
            ], -1)
          ]), 2))
        : (_openBlock(), _createElementBlock("span", _hoisted_2)),
      (showCheckbox.value)
        ? (_openBlock(), _createBlock(MostaCheckbox, {
            key: 2,
            class: "rec-ref__check",
            "model-value": selected.value,
            "onUpdate:modelValue": onCheckboxUpdate,
            onClick: _cache[1] || (_cache[1] = _withModifiers(() => {}, ["stop"]))
          }, null, 8, ["model-value"]))
        : _createCommentVNode("", true),
      _createElementVNode("div", _hoisted_3, [
        (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(nameSpans.value, (span, index) => {
          return (_openBlock(), _createElementBlock("span", {
            key: index,
            style: _normalizeStyle(span.style)
          }, _toDisplayString(span.text), 5))
        }), 128))
      ])
    ], 6)
  ]))
}
}

})